// src/components/features/Structure/components/StructureStep2SoilType.jsx
// Step 2: Soil Type + Soil Condition — simple Radio card style

import {
  Box,
  Flex,
  Text,
  Radio,
  Button,
  VStack,
} from "@chakra-ui/react";

const SOIL_TYPES = [
  { value: "black",  label: "Black Soil",  sub: "Expansive clay — shrinks/swells with moisture" },
  { value: "red",    label: "Red Soil",    sub: "Iron-rich laterite — good drainage" },
  { value: "yellow", label: "Yellow Soil", sub: "Sandy / alluvial — requires compaction" },
];

const SOIL_CONDITIONS = [
  { value: "loose",  label: "Loose",  sub: "Low bearing capacity — deep foundation may be needed" },
  { value: "medium", label: "Medium", sub: "Standard foundation design applicable" },
  { value: "hard",   label: "Hard",   sub: "High bearing capacity — ideal for construction" },
];

function SelectCard({ value, label, sub, selected, onSelect }) {
  return (
    <Box
      border={selected ? "2px solid #319795" : "1px solid #E2E8F0"}
      borderRadius="10px"
      px={3}
      py={2}
      cursor="pointer"
      bg={selected ? "cyan.50" : "white"}
      transition="0.2s"
      _hover={{ borderColor: "cyan.400" }}
      onClick={() => onSelect(value)}
    >
      <Flex align="center" gap={3}>
        <Radio
          value={value}
          isChecked={selected}
          onChange={() => onSelect(value)}
          colorScheme="cyan"
          size="md"
        />
        <Box>
          <Text fontSize="14px" fontWeight="600" color="gray.800">{label}</Text>
          <Text fontSize="11px" color="gray.500">{sub}</Text>
        </Box>
      </Flex>
    </Box>
  );
}

export default function StructureStep2SoilType({
  formData, setFormData, onNext, onBack, isLastStep, onSubmit, viewMode,
}) {
  const soilType      = formData?.soilType      || "";
  const soilCondition = formData?.soilCondition || "";

  const handleNext = () => {
    if (!viewMode && (!soilType || !soilCondition)) return;
    if (isLastStep) onSubmit();
    else onNext();
  };

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <VStack spacing={4} align="stretch">
          <Box textAlign="center">
            <Text fontSize="17px" fontWeight="600" color="gray.800">Soil Type</Text>
            <Text fontSize="12px" color="gray.500" mt={1}>
              Select the type and condition of soil at your site
            </Text>
          </Box>

          {/* Soil Type */}
          <Box>
            <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Type of Soil</Text>
            <VStack spacing={2} align="stretch">
              {SOIL_TYPES.map((s) => (
                <SelectCard
                  key={s.value}
                  {...s}
                  selected={soilType === s.value}
                  onSelect={(v) => setFormData({ ...formData, soilType: v })}
                />
              ))}
            </VStack>
          </Box>

          {/* Soil Condition */}
          <Box>
            <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Condition of Soil</Text>
            <VStack spacing={2} align="stretch">
              {SOIL_CONDITIONS.map((c) => (
                <SelectCard
                  key={c.value}
                  {...c}
                  selected={soilCondition === c.value}
                  onSelect={(v) => setFormData({ ...formData, soilCondition: v })}
                />
              ))}
            </VStack>
          </Box>
        </VStack>
      </Box>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500"
          borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>
        <Button
          onClick={handleNext}
          isDisabled={viewMode ? false : (!soilType || !soilCondition)}
          variant="solid"
          bg={(viewMode || (soilType && soilCondition)) ? "cyan.500" : "gray.300"}
          color="white" borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: (viewMode || (soilType && soilCondition)) ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}